import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BRAND_MINT } from '@/lib/utils'
import {
  getOrgSsoConfigApi,
  upsertOrgSsoConfigApi,
  deleteOrgSsoConfigApi,
  testSsoConnectionApi,
  type SsoConnection,
  type SsoConfigParams,
} from '@/lib/api'
import {
  LoaderIcon,
  ShieldCheckIcon,
  SaveIcon,
  Trash2Icon,
  PlayIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InfoIcon,
  UploadIcon,
  ExternalLinkIcon,
} from 'lucide-react'

// ── Helpers ──────────────────────────────────────────────────────────

function parseJsonField(input: string): string[] {
  const trimmed = input.trim()
  if (!trimmed) return []
  // Try comma-separated or newline-separated
  return trimmed
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function parseAttributeMapping(input: string): Record<string, string> | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  // Accept key=value per line format
  const mapping: Record<string, string> = {}
  for (const line of trimmed.split('\n')) {
    const eqIdx = line.indexOf('=')
    if (eqIdx > 0) {
      mapping[line.slice(0, eqIdx).trim()] = line.slice(eqIdx + 1).trim()
    }
  }
  return Object.keys(mapping).length > 0 ? mapping : null
}

function formatJsonArray(arr: string[] | null | undefined): string {
  if (!arr || !Array.isArray(arr)) return ''
  return arr.join('\n')
}

function formatAttributeMapping(mapping: Record<string, string> | null | undefined): string {
  if (!mapping) return ''
  return Object.entries(mapping)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n')
}

// ── Page ─────────────────────────────────────────────────────────────

export const Route = createFileRoute('/dashboard/admin/sso')({
  component: () => {
    const [orgId, setOrgId] = useState('')
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)
    const [disabling, setDisabling] = useState(false)
    const [config, setConfig] = useState<SsoConnection | null>(null)
    const [ssoConfigured, setSsoConfigured] = useState(false)
    const [certWarning, setCertWarning] = useState<{
      expires_at: string
      days_until_expiry: number
      severity: string
      message: string
    } | null>(null)
    const [testResults, setTestResults] = useState<Array<{
      check: string
      status: 'ok' | 'warning' | 'error'
      value: string
    }> | null>(null)

    // Form state
    const [protocol, setProtocol] = useState<'saml' | 'oidc'>('saml')
    const [idpEntityId, setIdpEntityId] = useState('')
    const [idpSsoUrl, setIdpSsoUrl] = useState('')
    const [idpSloUrl, setIdpSloUrl] = useState('')
    const [idpMetadataXml, setIdpMetadataXml] = useState('')
    const [idpCert, setIdpCert] = useState('')
    const [nameIdFormat, setNameIdFormat] = useState('emailAddress')
    const [signAuthn, setSignAuthn] = useState(true)
    const [attrMapping, setAttrMapping] = useState('')
    const [oidcClientId, setOidcClientId] = useState('')
    const [oidcClientSecret, setOidcClientSecret] = useState('')
    const [oidcDiscoveryUrl, setOidcDiscoveryUrl] = useState('')
    const [emailDomains, setEmailDomains] = useState('')
    const [jitMode, setJitMode] = useState<'auto' | 'pre_approved' | 'disabled'>('disabled')
    const [jitRole, setJitRole] = useState('employee')
    const [preApprovedEmails, setPreApprovedEmails] = useState('')

    // Populate form from config
    function populateForm(c: SsoConnection) {
      setProtocol(c.protocol)
      setIdpEntityId(c.idp_entity_id || '')
      setIdpSsoUrl(c.idp_sso_url || '')
      setIdpSloUrl(c.idp_slo_url || '')
      setIdpCert(c.idp_x509_certificate || '')
      setNameIdFormat(c.name_id_format || 'emailAddress')
      setSignAuthn(c.want_authn_requests_signed !== false)
      setAttrMapping(formatAttributeMapping(c.saml_attribute_mapping))
      setOidcClientId(c.oidc_client_id || '')
      setOidcClientSecret('')
      setOidcDiscoveryUrl(c.oidc_discovery_url || '')
      setEmailDomains(formatJsonArray(c.email_domains))
      setJitMode(c.jit_provisioning_mode || 'disabled')
      setJitRole(c.jit_default_role || 'employee')
      setPreApprovedEmails(formatJsonArray(c.pre_approved_emails))
    }

    // Load config
    const loadConfig = async (id: number) => {
      setLoading(true)
      setConfig(null)
      setCertWarning(null)
      setTestResults(null)
      try {
        const res = await getOrgSsoConfigApi(id)
        if (res.sso_configured && res.config) {
          setConfig(res.config)
          setSsoConfigured(true)
          populateForm(res.config)
        } else {
          setConfig(null)
          setSsoConfigured(false)
          // Reset form
          setIdpEntityId('')
          setIdpSsoUrl('')
          setIdpSloUrl('')
          setIdpMetadataXml('')
          setIdpCert('')
          setNameIdFormat('emailAddress')
          setSignAuthn(true)
          setAttrMapping('')
          setOidcClientId('')
          setOidcClientSecret('')
          setOidcDiscoveryUrl('')
          setEmailDomains('')
          setJitMode('disabled')
          setJitRole('employee')
          setPreApprovedEmails('')
        }
        if (res.certificate_warning) {
          setCertWarning(res.certificate_warning)
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load SSO config')
      } finally {
        setLoading(false)
      }
    }

    const handleSave = async () => {
      const id = parseInt(orgId, 10)
      if (!id) {
        toast.error('Please enter a valid organization ID')
        return
      }

      if (!emailDomains.trim()) {
        toast.error('At least one email domain is required')
        return
      }

      setSaving(true)
      try {
        const params: SsoConfigParams = {
          protocol,
          email_domains: parseJsonField(emailDomains),
          idp_entity_id: idpEntityId || undefined,
          idp_sso_url: idpSsoUrl || undefined,
          idp_slo_url: idpSloUrl || undefined,
          jit_provisioning_mode: jitMode,
          jit_default_role: jitRole,
        }

        if (protocol === 'saml') {
          if (idpMetadataXml.trim()) {
            params.idp_metadata_xml = idpMetadataXml
          }
          if (idpCert.trim()) {
            params.idp_x509_certificate = idpCert
          }
          params.name_id_format = nameIdFormat
          params.want_authn_requests_signed = signAuthn
          const mapping = parseAttributeMapping(attrMapping)
          if (mapping) params.saml_attribute_mapping = mapping
        }

        if (protocol === 'oidc') {
          if (oidcClientId.trim()) params.oidc_client_id = oidcClientId
          if (oidcClientSecret.trim()) params.oidc_client_secret = oidcClientSecret
          if (oidcDiscoveryUrl.trim()) params.oidc_discovery_url = oidcDiscoveryUrl
        }

        if (jitMode === 'pre_approved') {
          const emails = parseJsonField(preApprovedEmails)
          if (emails.length > 0) params.pre_approved_emails = emails
        }

        const res = await upsertOrgSsoConfigApi(id, params)
        toast.success(res.message)
        await loadConfig(id)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save SSO config')
      } finally {
        setSaving(false)
      }
    }

    const handleDisable = async () => {
      const id = parseInt(orgId, 10)
      if (!id) return

      setDisabling(true)
      try {
        const res = await deleteOrgSsoConfigApi(id)
        toast.success(res.message)
        setConfig(null)
        setSsoConfigured(false)
        setCertWarning(null)
        setTestResults(null)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to disable SSO')
      } finally {
        setDisabling(false)
      }
    }

    const handleTest = async () => {
      const id = parseInt(orgId, 10)
      if (!id) return

      setTesting(true)
      setTestResults(null)
      try {
        const res = await testSsoConnectionApi(id)
        setTestResults(res.results)

        if (res.status === 'ok') {
          toast.success('SSO connection test passed')
        } else if (res.status === 'warning') {
          toast.warning('SSO connection test completed with warnings')
        } else {
          toast.error('SSO connection test failed')
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Test connection failed')
      } finally {
        setTesting(false)
      }
    }

    // ── Render ───────────────────────────────────────────────────────

    return (
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">SSO Configuration</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Configure SAML 2.0 or OpenID Connect SSO for an organization.
              </p>
            </div>
          </div>
        </div>

        {/* Org ID input */}
        <div className="mb-6 rounded-xl border border-border bg-card p-5">
          <Label htmlFor="orgId" className="text-sm font-semibold">
            Organization ID
          </Label>
          <p className="mb-3 text-xs text-muted-foreground">
            Enter the organization ID to manage its SSO configuration.
          </p>
          <div className="flex gap-2">
            <Input
              id="orgId"
              type="number"
              placeholder="e.g. 42"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="max-w-[160px]"
            />
            <Button
              onClick={() => loadConfig(parseInt(orgId, 10))}
              disabled={loading || !orgId}
              variant="outline"
            >
              {loading ? (
                <LoaderIcon className="size-4 animate-spin" />
              ) : (
                'Load'
              )}
            </Button>
            {ssoConfigured && (
              <>
                <Button
                  onClick={handleTest}
                  disabled={testing}
                  variant="outline"
                  className="gap-1.5"
                >
                  {testing ? (
                    <LoaderIcon className="size-4 animate-spin" />
                  ) : (
                    <PlayIcon className="size-4" />
                  )}
                  Test
                </Button>
                <Button
                  onClick={handleDisable}
                  disabled={disabling}
                  variant="outline"
                  className="gap-1.5 text-red-500 hover:text-red-600"
                >
                  {disabling ? (
                    <LoaderIcon className="size-4 animate-spin" />
                  ) : (
                    <Trash2Icon className="size-4" />
                  )}
                  Disable
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading SSO configuration…</p>
          </div>
        )}

        {/* Certificate expiry warning */}
        {certWarning && (
          <div
            className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${
              certWarning.severity === 'critical'
                ? 'border-red-500/30 bg-red-500/10 text-red-400'
                : certWarning.severity === 'warning'
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                  : 'border-blue-500/30 bg-blue-500/10 text-blue-400'
            }`}
          >
            <AlertTriangleIcon className="size-5 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold">{certWarning.message}</p>
              <p className="mt-1 text-xs opacity-70">
                Update the IdP certificate before it expires to prevent login disruptions.
              </p>
            </div>
          </div>
        )}

        {/* Test results */}
        {testResults && (
          <div className="mb-6 rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 text-sm font-semibold">Connection Test Results</h3>
            <div className="space-y-2">
              {testResults.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {r.status === 'ok' ? (
                    <CheckCircleIcon className="size-4 shrink-0 text-green-500 mt-0.5" />
                  ) : r.status === 'warning' ? (
                    <AlertTriangleIcon className="size-4 shrink-0 text-amber-500 mt-0.5" />
                  ) : (
                    <XCircleIcon className="size-4 shrink-0 text-red-500 mt-0.5" />
                  )}
                  <div>
                    <span className="font-medium">{r.check}</span>
                    <span className="ml-2 text-muted-foreground">{r.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Configuration form */}
        {!loading && orgId && (
          <div className="space-y-6">
            {/* Protocol selection */}
            <div className="rounded-xl border border-border bg-card p-5">
              <Label className="text-sm font-semibold">Protocol</Label>
              <p className="mb-3 text-xs text-muted-foreground">
                Choose between SAML 2.0 (Okta, Azure AD, Ping, OneLogin) and OpenID Connect (Google Workspace, modern IdPs).
              </p>
              <div className="flex gap-3">
                <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                  protocol === 'saml' ? 'border-[#00E5A0] bg-[#00E5A0]/10 text-[#00E5A0]' : 'border-border text-muted-foreground hover:border-[#00E5A0]/30'
                }`}>
                  <input type="radio" name="protocol" value="saml" checked={protocol === 'saml'} onChange={() => setProtocol('saml')} className="sr-only" />
                  SAML 2.0
                </label>
                <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                  protocol === 'oidc' ? 'border-[#00E5A0] bg-[#00E5A0]/10 text-[#00E5A0]' : 'border-border text-muted-foreground hover:border-[#00E5A0]/30'
                }`}>
                  <input type="radio" name="protocol" value="oidc" checked={protocol === 'oidc'} onChange={() => setProtocol('oidc')} className="sr-only" />
                  OpenID Connect
                </label>
              </div>
            </div>

            {/* SAML-specific fields */}
            {protocol === 'saml' && (
              <>
                <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                  <h3 className="text-sm font-semibold">IdP Metadata</h3>
                  <p className="text-xs text-muted-foreground">
                    Paste your IdP's metadata XML to auto-fill fields, or enter them manually.
                  </p>
                  <div>
                    <Label htmlFor="metadataXml" className="text-xs font-medium">Metadata XML (auto-fill)</Label>
                    <textarea
                      id="metadataXml"
                      value={idpMetadataXml}
                      onChange={(e) => setIdpMetadataXml(e.target.value)}
                      placeholder="<md:EntityDescriptor entityID=...>"
                      rows={4}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-[#00E5A0] mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="idpEntityId" className="text-xs font-medium">IdP Entity ID</Label>
                      <Input id="idpEntityId" value={idpEntityId} onChange={(e) => setIdpEntityId(e.target.value)} placeholder="https://idp.example.com" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="idpSsoUrl" className="text-xs font-medium">Single Sign-On URL</Label>
                      <Input id="idpSsoUrl" value={idpSsoUrl} onChange={(e) => setIdpSsoUrl(e.target.value)} placeholder="https://idp.example.com/sso" className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="idpSloUrl" className="text-xs font-medium">Single Logout URL (optional)</Label>
                    <Input id="idpSloUrl" value={idpSloUrl} onChange={(e) => setIdpSloUrl(e.target.value)} placeholder="https://idp.example.com/slo" className="mt-1" />
                  </div>

                  <div>
                    <Label htmlFor="idpCert" className="text-xs font-medium">X.509 Certificate (PEM)</Label>
                    <textarea
                      id="idpCert"
                      value={idpCert}
                      onChange={(e) => setIdpCert(e.target.value)}
                      placeholder="-----BEGIN CERTIFICATE----- ..."
                      rows={4}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-[#00E5A0] mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nameIdFormat" className="text-xs font-medium">Name ID Format</Label>
                      <select
                        id="nameIdFormat"
                        value={nameIdFormat}
                        onChange={(e) => setNameIdFormat(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-[#00E5A0]"
                      >
                        <option value="emailAddress">emailAddress</option>
                        <option value="unspecified">unspecified</option>
                        <option value="persistent">persistent</option>
                        <option value="transient">transient</option>
                      </select>
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={signAuthn} onChange={(e) => setSignAuthn(e.target.checked)} className="size-4 rounded border-border" />
                        Sign AuthnRequests
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="attrMapping" className="text-xs font-medium">SAML Attribute Mapping (key=value per line)</Label>
                    <textarea
                      id="attrMapping"
                      value={attrMapping}
                      onChange={(e) => setAttrMapping(e.target.value)}
                      placeholder="first_name=givenName&#10;last_name=surname&#10;role=groups"
                      rows={3}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-[#00E5A0] mt-1"
                    />
                  </div>
                </div>

                {/* SP Metadata URL */}
                {config && (
                  <div className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-center gap-2 text-sm">
                      <InfoIcon className="size-4 text-muted-foreground" />
                      <span className="font-medium">SP Metadata URL:</span>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded">
                        /api/v1/auth/sso/saml/metadata/{config.sso_connection_id}
                      </code>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* OIDC-specific fields */}
            {protocol === 'oidc' && (
              <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <h3 className="text-sm font-semibold">OIDC Configuration</h3>

                <div>
                  <Label htmlFor="oidcDiscoveryUrl" className="text-xs font-medium">Discovery URL</Label>
                  <Input id="oidcDiscoveryUrl" value={oidcDiscoveryUrl} onChange={(e) => setOidcDiscoveryUrl(e.target.value)} placeholder="https://accounts.google.com/.well-known/openid-configuration" className="mt-1" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="oidcClientId" className="text-xs font-medium">Client ID</Label>
                    <Input id="oidcClientId" value={oidcClientId} onChange={(e) => setOidcClientId(e.target.value)} placeholder="your-client-id" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="oidcClientSecret" className="text-xs font-medium">Client Secret</Label>
                    <Input id="oidcClientSecret" type="password" value={oidcClientSecret} onChange={(e) => setOidcClientSecret(e.target.value)} placeholder={config ? '••••••••' : 'Enter secret'} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="oidcIssuer" className="text-xs font-medium">Issuer URL</Label>
                  <Input id="oidcIssuer" value={idpEntityId} onChange={(e) => setIdpEntityId(e.target.value)} placeholder="https://accounts.google.com" className="mt-1" />
                </div>
              </div>
            )}

            {/* Common fields */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <h3 className="text-sm font-semibold">Domain &amp; Provisioning</h3>

              <div>
                <Label htmlFor="emailDomains" className="text-xs font-medium">Email Domains (one per line)</Label>
                <textarea
                  id="emailDomains"
                  value={emailDomains}
                  onChange={(e) => setEmailDomains(e.target.value)}
                  placeholder="acme.com&#10;*.acme.co.uk"
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[#00E5A0] mt-1"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Use <code>*.domain.com</code> for all subdomains. One entry per line.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="jitMode" className="text-xs font-medium">JIT Provisioning</Label>
                  <select
                    id="jitMode"
                    value={jitMode}
                    onChange={(e) => setJitMode(e.target.value as typeof jitMode)}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-[#00E5A0]"
                  >
                    <option value="disabled">Disabled</option>
                    <option value="auto">Auto (trust IdP)</option>
                    <option value="pre_approved">Pre-approved only</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="jitRole" className="text-xs font-medium">Default Role</Label>
                  <select
                    id="jitRole"
                    value={jitRole}
                    onChange={(e) => setJitRole(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-[#00E5A0]"
                  >
                    <option value="employee">employee</option>
                    <option value="company_admin">company_admin</option>
                    <option value="hr_manager">hr_manager</option>
                    <option value="payroll_admin">payroll_admin</option>
                  </select>
                </div>
              </div>

              {jitMode === 'pre_approved' && (
                <div>
                  <Label htmlFor="preApprovedEmails" className="text-xs font-medium">Pre-approved Emails (one per line)</Label>
                  <textarea
                    id="preApprovedEmails"
                    value={preApprovedEmails}
                    onChange={(e) => setPreApprovedEmails(e.target.value)}
                    placeholder="user1@acme.com&#10;user2@acme.com"
                    rows={3}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[#00E5A0] mt-1"
                  />
                </div>
              )}
            </div>

            {/* Save button */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {ssoConfigured ? 'Configuration loaded. Save changes or test the connection.' : 'Fill in the form and save to enable SSO.'}
              </p>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="gap-1.5"
                style={{ backgroundColor: BRAND_MINT, color: '#0B1120' }}
              >
                {saving ? (
                  <LoaderIcon className="size-4 animate-spin" />
                ) : (
                  <SaveIcon className="size-4" />
                )}
                {ssoConfigured ? 'Update' : 'Enable SSO'}
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  },
})
